// src/layouts/IrregularAllowances/IrregularAllowances.js
import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import { useNavigate } from "react-router-dom";

import {
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function IrregularAllowances() {
  const [items, setItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    id: null,
    code: "",
    name: "",
    description: "",
    requires_activity: false,
    is_active: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/irregular-allowances");
      setItems(response.data);
    } catch (err) {
      console.error("Failed to fetch irregular allowances", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentItem({
        id: item.id,
        code: item.code || "",
        name: item.name || "",
        description: item.description || "",
        requires_activity: !!item.requires_activity,
        is_active: item.is_active !== false,
      });
    } else {
      setCurrentItem({
        id: null,
        code: "",
        name: "",
        description: "",
        requires_activity: false,
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        code: currentItem.code,
        name: currentItem.name,
        description: currentItem.description,
        requires_activity: currentItem.requires_activity,
        is_active: currentItem.is_active,
      };

      if (currentItem.id) {
        await axios.put(`/irregular-allowances/${currentItem.id}`, payload);
        setSnackbar({ open: true, message: "Irregular allowance updated successfully" });
      } else {
        await axios.post("/irregular-allowances", payload);
        setSnackbar({ open: true, message: "Irregular allowance created successfully" });
      }

      fetchItems();
      setOpenDialog(false);
    } catch (err) {
      console.error(err);
      alert("Something went wrong while saving.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this allowance?")) return;

    try {
      await axios.delete(`/irregular-allowances/${id}`);
      fetchItems();
      setSnackbar({ open: true, message: "Irregular allowance deleted successfully" });
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  // Navigation helpers
  const gotoRules = (id) => {
    navigate(`/irregular-allowances/${id}/rules`);
  };

  const gotoRates = (id) => {
    navigate(`/irregular-allowances/${id}/rates`);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginated = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container justifyContent="space-between" alignItems="center" px={2} mb={2}>
          <Typography variant="h5">Irregular Allowances</Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog(null)}
            sx={{ color: "#fff" }}
          >
            Add New
          </Button>
        </Grid>

        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
          <Table>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ padding: 3 }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : paginated.length > 0 ? (
                paginated.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{item.code}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {item.requires_activity ? "Activity-based" : "Flat / Monthly"}
                    </TableCell>
                    <TableCell>{item.is_active ? "Active" : "Inactive"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1 }}
                        onClick={() => gotoRules(item.id)}
                      >
                        Rules
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1 }}
                        onClick={() => gotoRates(item.id)}
                      >
                        Rates
                      </Button>
                      <IconButton color="primary" onClick={() => handleOpenDialog(item)}>
                        <Icon>edit</Icon>
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(item.id)}>
                        <Icon>delete</Icon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ padding: 3 }}>
                    No irregular allowances found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={items.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      </MDBox>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {currentItem.id ? "Edit Irregular Allowance" : "Add New Irregular Allowance"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Code"
            fullWidth
            margin="normal"
            value={currentItem.code}
            onChange={(e) => setCurrentItem({ ...currentItem, code: e.target.value })}
          />
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={currentItem.name}
            onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            minRows={2}
            value={currentItem.description}
            onChange={(e) =>
              setCurrentItem({
                ...currentItem,
                description: e.target.value,
              })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={currentItem.requires_activity}
                onChange={(e) =>
                  setCurrentItem({ ...currentItem, requires_activity: e.target.checked })
                }
              />
            }
            label="Activity-based (e.g. Call Duty)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={currentItem.is_active}
                onChange={(e) => setCurrentItem({ ...currentItem, is_active: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default IrregularAllowances;
